import { useLocale } from '@hooks/useLocale';
import useSWR from 'swr';
import { MecVariant } from '@adapters/bigcommerce/types/product';

interface SpuItemAvailabilityType {
  lineItemId: string;
  sku: string;
  productName: string;
  store: {
    uid: string;
    data: {
      mec_store_id: string;
      store_name: string;
      address_province: string;
    };
  };
  availability: boolean;
  isSTS: boolean;
  quickPickup: boolean;
  message: { short: string; long: string };
  shippingEstimate: string;
  range: {
    start: number | null;
    end: number | null;
  };
}

interface SpuItemAvailabilityResponse {
  data: SpuItemAvailabilityType[];
  errors?: Array<{ message: string }>;
}

interface UseSpuItemAvailabilityVariantOptions {
  selectedVariant?: MecVariant;
  productName?: string;
  shippingRestrictions?: any;
  quantity: number | null;
}

const getSpuItemAvailabilityVariantFetcher = async (
  url: string,
  locale: string,
  selectedVariant: MecVariant,
  productName: string,
  shippingRestrictions?: any,
  quantity?: number | null
): Promise<SpuItemAvailabilityType[] | null> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': locale,
    },
    body: JSON.stringify({
      selectedVariant,
      productName,
      shippingRestrictions,
      quantity,
    }),
  });

  if (!response.ok) {
    console.error(
      'Variant API response not ok:',
      response.status,
      response.statusText
    );
    return null;
  }

  const result: SpuItemAvailabilityResponse = await response.json();

  if (result.errors && result.errors.length > 0) {
    throw new Error(result.errors[0].message);
  }

  return result.data;
};

export const useSpuItemAvailabilityVariant = (
  options?: UseSpuItemAvailabilityVariantOptions
) => {
  const locale = useLocale();
  const { selectedVariant, productName, shippingRestrictions, quantity } =
    options || {};

  // Create a unique key that includes all parameters for proper caching
  const shouldFetch = Boolean(selectedVariant && productName);
  const swrKey = shouldFetch
    ? `/api/variant/spu-item-availability-${locale}-${
        selectedVariant?.entityId
      }-${productName}-${JSON.stringify(shippingRestrictions)}-${
        quantity ?? null
      }`
    : null;

  const {
    data,
    error: swrError,
    isValidating,
    mutate,
  } = useSWR<SpuItemAvailabilityType[] | null>(
    swrKey,
    () =>
      selectedVariant && productName
        ? getSpuItemAvailabilityVariantFetcher(
            '/api/variant/spu-item-availability',
            locale || 'en',
            selectedVariant,
            productName,
            shippingRestrictions,
            quantity ?? null
          )
        : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      shouldRetryOnError: false,
    }
  );

  // Handle validation error when required parameters are missing
  const error =
    swrError?.message ||
    (!selectedVariant || !productName
      ? 'selectedVariant and productName are required'
      : null);

  return {
    data,
    loading: isValidating,
    error,
    refetch: mutate,
  };
};
